import cx from 'classnames';
import NextLink from 'next/link';

import {Icon, TallyLogo, Text} from '#component/base';
import {TextProps} from '#component/base/Text';
import {sprinkles} from '#style/sprinkles';

import {FooterLink as IFooterLink, FooterProps} from './Footer.types';

import * as css from './Footer.css';

const authorizedFooterLinks: IFooterLink[][] = [
  [
    {href: '/dashboard', children: "What's New"},
    {href: '/mytallyage', children: 'My TallyAge™'},
    {href: '/actionplan', children: 'Action Plan', membershipOnly: true},
    {href: '/profile', children: 'My Account'},
  ],
  [
    {href: '/shop/pages/our-science', children: 'Science'},
    {href: '/shop/collections/supplements', children: 'Supplements'},
    {href: '/shop/collections/bundles', children: 'Bundles'},
    {href: '/shop/collections', children: 'Shop'},
  ],
  [
    {href: '/shop/blogs/learn', children: 'Blog'},
    {
      href: '/shop/pages/referrals',
      children: 'Refer a Friend',
    },
    {href: '/register', children: 'Register Your Test'},
    {
      children: 'Help Center',
      href: process.env.NEXT_PUBLIC_HELP_CENTER_URL || '',
      rel: 'noreferrer noopener',
      target: '_blank',
    },
    {
      children: 'Contact Us',
      href: process.env.NEXT_PUBLIC_CONTACT_URL || '',
      target: '_blank',
    },
  ],
];

const unauthorizedFooterLinks: IFooterLink[][] = [
  [
    {href: '/shop/pages/our-science', children: 'Science'},
    {href: '/shop/collections/supplements', children: 'Supplements'},
    {href: '/shop/collections', children: 'Shop'},
    {href: '/shop/pages/referrals', children: 'Refer a Friend'},
  ],
  [
    {href: '/shop/blogs/learn', children: 'Blog'},
    {href: '/register', children: 'Register Your Test'},
    {
      children: 'Help Center',
      href: process.env.NEXT_PUBLIC_HELP_CENTER_URL || '',
      rel: 'noreferrer noopener',
      target: '_blank',
    },
    {
      children: 'Contact Us',
      href: process.env.NEXT_PUBLIC_CONTACT_URL || '',
      rel: 'noreferrer noopener',
      target: '_blank',
    },
  ],
];

const socialLinks = [
  {
    href: 'https://www.instagram.com/tallyhealth',
    iconProps: {name: 'InstagramLogo'},
    title: 'Instagram',
  },
  {
    href: 'https://twitter.com/tally_health',
    iconProps: {name: 'TwitterLogo', weight: 'fill'},
    title: 'Twitter',
  },
] as const;

const legalLinks = [
  {href: '/shop/policies/privacy-policy', children: 'Privacy Policy'},
  {href: '/shop/policies/terms-of-service', children: 'Terms of Service'},
];

const FooterLink = ({className, href = '', ...restProps}: TextProps<'a'>) => (
  <NextLink href={href} passHref>
    <Text as="a" className={cx(css.link, className)} color="deepBurgundy" {...restProps} />
  </NextLink>
);

export const Footer: React.FC<FooterProps> = ({
  isAuthenticated,
  hasActiveSubscription,
  hasMembership,
}: FooterProps) => {
  const featureFlagMap: Record<string, boolean> = {};

  const footerLinks = isAuthenticated ? authorizedFooterLinks : unauthorizedFooterLinks;
  return (
    <div className={css.root}>
      <div className={css.contentWrapper}>
        <div className={css.footerLinks}>
          <NextLink passHref href="/shop">
            <a aria-label="Tally Health™">
              <TallyLogo className={css.logo} isResponsive />
            </a>
          </NextLink>

          <nav className={css.nav}>
            {footerLinks.map((links, i) => (
              <div
                className={sprinkles({dir: 'col', flex: 'flex', gap: 32, items: 'start'})}
                key={i}>
                {links.map(
                  ({featureFlag, activeSubscriptionOnly, membershipOnly, ...props}, innerI) => {
                    if (
                      (activeSubscriptionOnly && !hasActiveSubscription) ||
                      (membershipOnly && !hasMembership) ||
                      (!!featureFlag && !featureFlagMap[featureFlag])
                    ) {
                      return null;
                    }
                    return <FooterLink key={`${i}-${innerI}`} {...props} />;
                  },
                )}
              </div>
            ))}

            <div className={sprinkles({flex: 'flex', gap: 24, dir: 'row'})}>
              {socialLinks.map(({iconProps, ...props}, i) => (
                <a
                  className={css.socialLink}
                  key={i}
                  rel="noreferrer noopener"
                  target="_blank"
                  {...props}>
                  <Icon {...iconProps} color="burgundy" />
                </a>
              ))}
            </div>
          </nav>
        </div>

        <div>
          <div className={css.legalInfo}>
            <div className={css.legalLinks}>
              {legalLinks.map((props, i) => (
                <FooterLink className={css.legalInfoContent} key={i} {...props} />
              ))}
            </div>

            <Text className={css.legalInfoContent} color="deepBurgundy">
              {`© Tally Health™ ${new Date().getFullYear()}`}
            </Text>
          </div>

          {/* TODO: remove on March 24th, 2024 */}
          <Text variant="caption" color="doveGray" as="div" className={sprinkles({mT: [24, 48]})}>
            Our Terms of Service will be updated as of January 24, 2024. Read our updated Terms of
            Service{' '}
            <NextLink href="/shop/policies/terms-of-service" passHref>
              <Text
                as="a"
                variant="caption"
                color="doveGray"
                className={sprinkles({textDecoration: 'underline'})}
                target="__blank">
                here
              </Text>
            </NextLink>
            .
          </Text>
          <Text variant="caption" color="doveGray" as="div">
            These statements have not been evaluated by the Food and Drug Administration. These
            products are not intended to diagnose, treat, cure, or prevent any disease.
          </Text>
        </div>
      </div>
    </div>
  );
};
